import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';

import { Clock } from '../../runtime/clock/clock';
import { PlaybackService } from '../../runtime/playback/playback.service';
import { SignageRuntimeService } from '../../runtime/signage-runtime.service';
import { SlideLayout } from '../../runtime/slides/enums';
import { RuntimeSlideModel } from '../../runtime/slides/models';
import { PresentationController } from '../presentation-controller';
import { PriorityBannerComponent } from '../priority-banner/priority-banner.component';
import { FETCHING_SLIDE, NO_CONTENT_SLIDE, NO_INTERNET_SLIDE, SLIDE_LAYOUTS } from '../slide-layouts.record';

@Component({
  selector: 'ds-slide-host',
  imports: [NgComponentOutlet, PriorityBannerComponent],
  templateUrl: './slide-host.component.html',
  styleUrls: ['./slide-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SlideHostComponent {
  private readonly playback = inject(PlaybackService);
  private readonly runtime = inject(SignageRuntimeService);
  private readonly clock = inject(Clock);
  private readonly presentation = signal<PresentationController | null>(null);
  private detach?: () => void;

  public readonly controller = this.presentation.asReadonly();
  public readonly layout = computed(() => SLIDE_LAYOUTS[this.controller()?.slide.layout ?? SlideLayout.Backdrop]);
  public readonly inputs = computed(() => ({ controller: this.controller() }));
  public readonly priorityMessages = this.runtime.priorityMessages;

  public constructor() {
    effect(() => {
      const slide = this.playback.current();
      const generation = this.playback.generation();
      const status = slide ? null : this.status();
      untracked(() => this.present(slide ?? status, slide ? generation : null));
    });
    inject(DestroyRef).onDestroy(() => this.teardown());
  }

  private status(): RuntimeSlideModel {
    switch (this.runtime.state()) {
      case 'starting': return FETCHING_SLIDE;
      case 'offline': return NO_INTERNET_SLIDE;
      default: return NO_CONTENT_SLIDE;
    }
  }

  private present(slide: RuntimeSlideModel | null, generation: number | null): void {
    this.teardown();

    if (!slide) {
      return;
    }

    const controller = new PresentationController(slide, this.clock);
    this.presentation.set(controller);
    // Sealing immediately is safe: the slide's own duration keeps it pending while children register their waits.
    controller.ready();

    if (generation !== null) {
      this.detach = this.playback.attachController(controller, slide.id, generation);
    }
  }

  private teardown(): void {
    this.detach?.();
    this.detach = undefined;
    this.presentation()?.destroy();
    this.presentation.set(null);
  }
}
